import { useState, useEffect } from 'react';
import { ClassConstructor, plainToClass } from 'class-transformer';
import { validateOrReject } from 'class-validator';
import { GenericRequest } from 'data-layer/types';
import { fetch, fetchInstance, abortController } from 'data-layer/fetch-api/fetch';

export type FetchResult<I> = [
  data: I | null,
  loading: boolean,
  error: string | null,
  cancelFetch: () => void,
];

function useFetch<T, K extends GenericRequest>(request: K, Dto: ClassConstructor<T>, hash: string): FetchResult<T> {
  const {url, data: postData = null, method = 'POST' } = request;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cancelFetch = () => abortController.abort();

  useEffect(() => {
    (async () => {
      try {
        const { data: responseData } = await fetch(
          fetchInstance,
          url,
          postData,
          {
            signal: abortController.signal,
            method,
            hash,
          },
        );
        if (!responseData.success) {
          throw new Error(responseData.message || 'Server returned error.');
        }
        const responseDto = plainToClass(Dto, responseData.data) as {};
        await validateOrReject(responseDto);
        setData(responseData.data);
      } catch (err) {
        setError((err as any).message || 'We are sorry, but I can\'t fulfill your request at the moment.');
      } finally {
        setLoading(false);
      }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ data, loading, error, cancelFetch ];
}

export default useFetch;
